import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  combineLatest,
  EMPTY,
  from,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementEvent, UpdatePropRequest } from '@blaze-runtime/engine-domain';
import {
  ChartValueUpdate,
  ChartVariantType,
  DEFAULT_ELEMENT_PADDING,
  ElementHorizontalAlignment,
  getBackground,
  getValueForAlias,
  isGraphicalChartUpdateData,
  isSingleChartUpdateData,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  makeFontStyles,
  makeFontStyleStyles,
  makeLabelHorizontalAlignStyles,
  PropertiesConfigInputColorPaletteModel,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { findColumn } from '@blaze-shared/schema-domain';
import {
  BlazeSchema,
  DatasetColumn,
  DatasetRow,
  type ElementSchema,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  adjustColorBrightness,
  deepClone,
  DEFAULT_NUMBER_PRECISION,
  delayUntil,
  isEmptyString,
  isNullish,
  mutationRxUtils,
  resizeRxUtils,
  useEmitTracker,
} from '@blaze-shared/shared-util';
import { isAggregateNode, isComputeNode } from '@blaze-shared/workflow-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLinkService } from '../../services/element-link.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { ChartPropsModelData, ChartValuePropsModel, DotChartIcons } from './chart.models';
import { ChartService } from './chart.service';
import { UnsortedDataMeta } from './models/helper.types';
import { fillAliasInMultiSeries, getChartAliasForMainValue } from './utils/alias.utils';
import {
  allDataWithTheSameMonth,
  allDataWithTheSameYear,
  ComputeKeys,
  fillDataRowsWithMissingDays,
  fillDataRowsWithMissingMonths,
  fillDataRowsWithMissingQuarters,
  fillDataRowsWithMissingWeeks,
  fillDataRowsWithMissingYears,
  fillTimeRowsWithMissingMinutes,
  fixDataRowsComputeZeros,
  getKeysToReadDataFromValue,
  getMissingValueForChartType,
  getMonthColumnName,
  valueFromWorkflow,
} from './utils/chart-data.utils';
import { prepareDatasetMeta } from './utils/dataset-meta.utils';
import {
  compareXAxis,
  EMPTY_DATE_LABEL,
  getDateFormatParts,
  getLabelForChart,
  isTimeOnlyFormatter,
} from './utils/labels.utils';

type DeltaIconClassType = 'rotate-45' | '-rotate-45' | '';

@UntilDestroy()
@Component({
  selector: 'be-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChartComponent<T extends ChartVariantType>
  extends CanvasElementBaseComponent<T>
  implements CanvasElementComponent<T>, OnInit, OnChanges
{
  private readonly elementLinkService = inject(ElementLinkService);
  private readonly intelliSenseService = inject(IntelliSenseService);
  private readonly host = inject(ElementRef);
  private readonly chartService = inject(ChartService);

  @Input() value: any = undefined;
  @Input() optionsList?: DatasetRow[] = [];

  @Output() updateProp = new EventEmitter<UpdatePropRequest>();

  @ViewChild('scaleCalcElement') scaleCalcElement!: ElementRef;
  @ViewChild('titleDescriptionEl') titleDescriptionEl!: ElementRef;
  @ViewChild('singleValueChartEl') singleValueChartEl!: ElementRef;

  @HostBinding('style.--b-cha-internal-padding') internalPadding? = DEFAULT_ELEMENT_PADDING;

  private readonly updateElementType$ = new Subject<ChartVariantType>();
  private readonly valueChanged$ = new Subject<ChartValueUpdate>();
  private readonly stylesChanged$ = new Subject();
  private readonly outputEmitTracker = useEmitTracker();
  private readonly triggerUpdate$ = new Subject<ChartValueUpdate>();
  private wasEmitted = false;

  readonly elementKeyRoot = 'b-cha';
  elementKey!: string;

  noData = false;

  labelEnabled!: boolean;
  labelText!: string;
  icon!: string;

  title?: string;
  description?: string;
  xDescription?: string;
  yDescription?: string;
  showTotal?: boolean;
  valueAlign?: ElementHorizontalAlignment;

  /**
   * Image is not supported for now.
   * @deprecated
   */
  image?: boolean | string;
  imagePosition?: boolean | string;

  isMainValueVisible: boolean | undefined;
  mainValue: number | undefined = 0;
  mainValueCompareFn: string | undefined;
  // BL-2584
  // compareValue: number | undefined = 0;
  // compareValueCompareFn: string | undefined;

  fieldPrecision?: number;
  fieldSuffix?: string;
  fieldFormatter: TableColumnFormat = 'number';

  delta?: boolean | 'percent' | 'value';
  deltaValue = 0;
  deltaDisplayValue = 0;
  isDeltaInfinity = false;
  deltaIconClass?: DeltaIconClassType;

  colorMainValue?: boolean;
  colorChartOutline?: boolean;
  alertLogic?: boolean | undefined;

  colorAlertDanger?: string;
  colorAlertNeutral?: string;
  colorAlertSuccess?: string;
  colorChartColor?: string;
  colorGeneralAxis?: string;
  descriptionColor?: string;
  alignValueChart?: string;

  deltaColor?: string;

  chartLabels?: string[];
  chartDatasets?: any[];
  chartPalette?: string;
  customChartPalette?: PropertiesConfigInputColorPaletteModel['customColors'];
  chartPaletteColors?: string[];
  chartGroupBy?: string;
  chartSegmentData?: string;
  chartGridLines?: string;
  chartGridDensity?: number;
  chartGridTimeDiff?: number;
  chartGroupByMonth?: boolean;
  chartBorderLine?: string;

  order?: 'x' | 'y';
  /** used for line, bar, column charts */
  minValue?: number;
  /** used for line, bar, column charts */
  maxValue?: number;
  curvedLines?: boolean;

  intlDateXFormatter!: Intl.DateTimeFormat;
  intlDateYFormatter!: Intl.DateTimeFormat;

  wrapperStyles: Record<string, string | undefined> | null = null;
  headerStyles!: Record<string, string>;
  headerTitleStyles!: Record<string, string>;
  headerIconStyles!: Record<string, string>;
  descriptionStyles!: Record<string, string>;
  valueStyles!: Record<string, string>;
  fontSizeEnabled: boolean;
  valueMinHeightEnabled: boolean;

  transformStyles$!: Observable<Record<string, any> | undefined>;

  get hideLegendAfterLines(): number | undefined {
    return this.props?.content?.chart?.hideLegendAfterLines;
  }

  get isLegacyH125(): boolean {
    return this.elementType === 'chartSingle' && this.props?.content.chart?.height === '125px';
  }

  constructor() {
    super();

    combineLatest({
      columns: this.getPropsColumns(),
      elementType: this.updateElementType$,
      theme: this.themesService.activeTheme$,
    })
      .pipe(
        // switchMap so a newer emission supersedes any in-flight async (dot) update:
        // without it the dot chart's awaited canvas pre-warming could resolve out of order
        // and commit stale datasets over a fresher render.
        switchMap(({ elementType, columns }) => {
          this.refreshChartPalette();

          switch (elementType) {
            case 'chartSingle':
              this.updateSingleValueChart(columns);
              return EMPTY;
            case 'chartPie':
            case 'chartDoughnut':
              this.updatePieChart(columns);
              return EMPTY;
            case 'chartLine':
            case 'chartBar':
            case 'chartColumn':
              this.updateGraphicalChart(columns);
              return EMPTY;
            case 'chartDot':
              return this.updateDotChart$(columns);
            default:
              return EMPTY;
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.valueChanged$.pipe(untilDestroyed(this)).subscribe((val) => {
      this.triggerUpdate$.next(val);
    });

    this.triggerUpdate$
      .pipe(
        delayUntil(() => this.getElementEventSchemaSet('select')),
        untilDestroyed(this),
      )
      .subscribe((val) => {
        this.updateOutput(val);
      });

    this.assignScaleForValue();

    this.initMarginHandler((props) =>
      this.isUsingOldPaddingStrategy ? '4px' : props?.style?.general?.offset?.margin,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldSkipThisChange(changes)) {
      return;
    }
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      this.updateContent();

      const currentData = changes?.props?.currentValue?.content?.data;
      const previousData = changes?.props?.previousValue?.content?.data;
      const isSchemaChangeDetected =
        currentData?.aggregation !== previousData?.aggregation ||
        previousData?.mainValue !== currentData?.mainValue ||
        previousData?.groupBy !== currentData?.groupBy ||
        previousData?.groupByDetails !== currentData?.groupByDetails ||
        previousData?.segmentData !== currentData?.segmentData;
      if (isSchemaChangeDetected) {
        this.setSchemaToOutput();
      }

      this.updateStyles();
      this.stylesChanged$.next(true);
    }

    // todo: refresh change todo on subject
    if (changes?.['props'] || changes?.['value']) {
      this.updateElementType$.next(this.elementType);
      this.updateValuesStyles();
    }
  }

  ngOnInit(): void {
    // When no binding assign default schema
    if (
      !this.bindingResults?.value ||
      !this.isChartConfigured(this.elementType, this.props?.content.data)
    ) {
      this.setDefaultEventSchema('select');
    }
  }

  emitClick(toolbarTriggered = false): void {
    if (this.isInEditor && !toolbarTriggered) {
      // Ignore if click was not triggered by the toolbar action in the editor
      return;
    }

    if (this.props?.content.link?.action) {
      this.elementLinkService.handleLinkTrigger(this.props?.content.link);
    }
  }

  chartPointClicked(data: ChartValueUpdate): void {
    this.valueChanged$.next(data);
  }

  private updateContent(): void {
    const { chart, title, format, alert } = this.props?.content || {};

    this.labelEnabled = title?.enabled ?? true;
    this.icon = title?.icon ?? '';
    this.title = title?.label;
    this.description = title?.description;
    this.xDescription = chart?.xDescription;
    this.yDescription = chart?.yDescription;
    this.showTotal = chart?.showTotal;
    this.fieldPrecision = isNullish(format?.precision)
      ? DEFAULT_NUMBER_PRECISION
      : format?.precision;
    this.fieldSuffix = format
      ? format.type === 'Percent'
        ? '%'
        : format.type === 'Currency'
          ? format.currency
          : ''
      : undefined;
    this.fieldFormatter =
      format?.type === 'Percent'
        ? 'percentage'
        : format?.type === 'Currency'
          ? 'currency'
          : 'number';
    this.delta = format?.delta && format.delta !== 'none' ? format.delta : false;
    this.colorMainValue = alert?.colorMainValue;
    this.colorChartOutline = alert?.colorChartOutline;
    this.alertLogic = alert?.logic && alert.logic === '+';

    const propsData = this.props?.data;
    if (propsData?.length && this.delta && !propsData[0].enabled) {
      this.delta = false;
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { alert, general, background, chart, titleBlock } = style || {};
    if (!style || !general || !background || !content?.font || !titleBlock) {
      return;
    }
    this.refreshDotChartGridDense();

    this.colorAlertDanger = alert?.danger.color1;
    this.colorAlertNeutral = alert?.neutral.color1;
    this.colorAlertSuccess = alert?.success.color1;
    this.colorChartColor = general?.value?.color1;
    this.colorGeneralAxis = general.axis?.color1;
    if (style.general && 'description' in style.general) {
      this.descriptionColor = style.general?.description?.color1;
    }
    this.alignValueChart =
      general.align === 'right' ? 'end' : general.align === 'center' ? 'center' : 'start';
    let chartGridLines = chart?.gridLines && getBackground(chart.gridLines);
    if (chartGridLines === 'none') {
      chartGridLines = 'transparent';
    }
    this.chartGridLines = chartGridLines;
    let chartBorderLine = chart?.line && getBackground(chart.line);
    if (chartBorderLine === 'none') {
      chartBorderLine = 'transparent';
    }
    this.chartBorderLine = chartBorderLine;

    this.refreshChartPalette(chart?.palette);
    this.wrapperStyles = {
      background: getBackground(background.fill),
      borderRadius: background.radius,
      ...makeBorderStyles(background.outline, background.width),
      ...makeContainerVisualEffectStyles(background.visualEffect),
    };
    this.headerStyles = {
      ...(makeLabelHorizontalAlignStyles(general.align) ?? undefined),
      color: general.title.color1,
      ...makeFontStyles(content.font),
      ...makeFontStyleStyles(content.font?.style),
      ['font-weight']: titleBlock?.fontWeight ?? 'var(--bat-headerFontWeight)',
    };

    this.headerTitleStyles = {
      fontSize: content.font.fontSize,
    };

    this.headerIconStyles = {
      '--icon-size': titleBlock.iconSize ?? 'var(--bat-headerIconSize)',
      color: content.title.iconColor?.color1 ?? '',
    };

    if (content.title?.iconPosition === 'right') {
      this.headerTitleStyles['flex-direction'] = 'row-reverse';
      this.headerIconStyles['margin-left'] = '-0.3em';
    }

    this.descriptionStyles = {
      color: general.description.color1,
      ...makeFontStyleStyles(content.font?.style), // force underline color
    };

    this.internalPadding = general.offset.padding;
  }

  private refreshChartPalette(palette?: PropertiesConfigInputColorPaletteModel): void {
    if (palette?.paletteName) {
      this.chartPalette = palette.paletteName;
    }
    if (palette?.customColors) {
      this.customChartPalette = palette.customColors;
    }

    if (!this.chartPalette) {
      return;
    }

    this.chartPaletteColors = new Array(10).fill('').map((_, index) => {
      return (
        this.customChartPalette?.[index] ||
        this.props?.content.data?.multiSeries?.[index]?.color?.color1 ||
        getComputedStyle(document.body).getPropertyValue(`--bat-${this.chartPalette}-bg-${index}`)
      );
    });
    this.cdr.markForCheck();
  }

  //#region single value
  private updateSingleValueChart(columns: DatasetColumn[]): void {
    const { data } = this.props?.content || {};
    const { aggregation /* BL-2584, compareValue, compareAggregation */ } = data || {};
    // BL-2584
    // this.compareValueCompareFn = compareAggregation;
    const { mainValue /* BL-2584, compareValue */ } = data || {};

    const dataRow = (Array.isArray(this.value) ? this.value[0] : this.value) ?? {};
    this.isMainValueVisible =
      !!mainValue ||
      aggregation === 'COUNT' ||
      (typeof dataRow === 'object' && Object.keys(dataRow).length > 0);

    const mainValueColumn = findColumn(columns, mainValue);
    const mainValueColumnName =
      mainValueColumn?.name || this.schemaService.getColumnName(mainValue);
    // const compareValueColumn = findColumn(columns, compareValue);

    if (aggregation === 'FIELD VALUE') {
      this.mainValue =
        (mainValue && dataRow[mainValue]) ||
        (mainValueColumnName && dataRow[mainValueColumnName]) ||
        undefined;
      // BL-2584
      // this.compareValue = compareValue ? dataRow[compareValue] : undefined;
    } else {
      this.mainValue = valueFromWorkflow(
        mainValueColumnName || mainValue,
        mainValueColumn?.id,
        aggregation,
        dataRow,
      );
      // BL-2584
      // this.compareValue = valueFromWorkflow(compareValue, compareAggregation, dataRow);
    }
    if (typeof this.mainValue !== 'number' || isNaN(this.mainValue)) {
      this.mainValue = 0;
    }
    // BL-2584
    // if (typeof this.compareValue !== 'number' || isNaN(this.compareValue)) {
    //   this.compareValue = 0;
    // }

    // BL-2584 (todo: convert it to use column Ids for blaze data table as source)
    // if (typeof this.mainValue === 'number' && typeof this.compareValue === 'number') {
    //   this.deltaValue = this.delta === 'percent'
    //     ? ((this.mainValue - this.compareValue) / this.compareValue * 100)
    //     : (this.mainValue - this.compareValue);
    //   this.deltaDisplayValue = Math.abs(this.deltaValue);
    //   if (this.deltaDisplayValue === Infinity) {
    //     this.isDeltaInfinity = true;
    //   } else {
    //     this.isDeltaInfinity = false;
    //   }
    // }

    const classIdx = !this.delta || !this.deltaValue ? 1 : 1 + Math.sign(this.deltaValue);
    this.deltaIconClass = ['rotate-45', '', '-rotate-45'][classIdx] as DeltaIconClassType;

    safeDetectChanges(this.cdr);
    this.valueChanged$.next(this.mainValue);
  }

  private assignScaleForValue(): void {
    this.transformStyles$ = combineLatest([
      mutationRxUtils.fromMutationObserver(this.host?.nativeElement, { childList: true }).pipe(
        startWith([]),
        switchMap(() => resizeRxUtils.fromResizeObserver(this.host.nativeElement)),
      ),
      this.props$.pipe(startWith(undefined)) as Observable<ChartValuePropsModel | undefined>,
      this.valueChanged$.pipe(startWith('0.00')),
      this.stylesChanged$.pipe(startWith(true)),
      this.isPending$,
    ]).pipe(
      switchMap(([, props]) => {
        this.updateValuesStyles();
        const { fontSizeEnabled } = props?.style?.valueFont || {};
        const { valueMinHeightEnabled } = props?.content?.options || {};
        const hostDimensions = getAbsoluteDimensions(this.scaleCalcElement.nativeElement);
        const titleDescDimensions = getAbsoluteDimensions(this.titleDescriptionEl?.nativeElement);

        const maxPossibleSize = {
          width: hostDimensions.width,
          height: hostDimensions.height - titleDescDimensions.height,
        };

        if (!this.singleValueChartEl?.nativeElement || fontSizeEnabled) {
          return of({ scale: 1 });
        }

        return resizeRxUtils.fromResizeObserver(this.singleValueChartEl.nativeElement).pipe(
          map((valueEntries) => {
            const currentSize = valueEntries[0].contentRect;
            const possibleScale =
              currentSize.width === 0 || currentSize.height === 0
                ? { x: maxPossibleSize.width, y: maxPossibleSize.height }
                : {
                    x: maxPossibleSize.width / currentSize.width,
                    y: maxPossibleSize.height / currentSize.height,
                  };

            let scale =
              possibleScale.x > possibleScale.y ? possibleScale.y : (possibleScale.x ?? 1);
            scale = valueMinHeightEnabled ? Math.max(scale, 1) : scale;

            return {
              scale,
            };
          }),
        );
      }),
    );
  }
  //#endregion

  //#region base chart
  /**
   * Builds the shared data model for all graphical charts (dot, line, bar, column).
   * Returns `null` when the chart is not configured enough to render.
   * This function have Customer Regression unit tests for BL-4195 & BL-4020.
   */
  private buildChartModel(columns: DatasetColumn[]): {
    unsortedDatasetMeta: UnsortedDataMeta[];
    customIcons: ChartPropsModelData['customIcons'];
  } | null {
    const { data } = this.props?.content || {};
    const {
      aggregation,
      groupByDetails,
      segmentDataDetails,
      order,
      direction,
      maxValue,
      minValue,
      customIcons,
    } = data || {};
    const { mainValue, groupBy, segmentData, multiSeries } = data || {};

    this.maxValue = isNullish(maxValue) || isEmptyString(maxValue) ? undefined : Number(maxValue);
    this.minValue = isNullish(minValue) || isEmptyString(minValue) ? undefined : Number(minValue);
    this.curvedLines = this.props?.content?.format?.curvedLines || false;

    const { aliasMap, mainAlias } = getChartAliasForMainValue(
      data?.alias,
      this.schemaService.getColumnName(mainValue),
      aggregation,
    );
    // Fulfill series names

    const multiSeriesValues = fillAliasInMultiSeries(
      multiSeries,
      aliasMap,
      Object.fromEntries(
        multiSeries?.map((series) => [
          series.mainValue,
          this.schemaService.getColumnName(series.mainValue),
        ]) || [],
      ),
    );

    if (!aggregation || !groupBy) {
      return null;
    }

    let dataRow = Array.isArray(this.value) ? deepClone(this.value) : [];
    const srcRow = dataRow[0];

    const computeKeys = getKeysToReadDataFromValue(srcRow);

    dataRow = fixDataRowsComputeZeros(dataRow, computeKeys);

    const isSameYear = allDataWithTheSameYear(srcRow, dataRow, computeKeys);
    const intlFormatterParts = getDateFormatParts(
      this.props?.content.format,
      isSameYear,
      undefined,
    );
    this.intlDateXFormatter = new Intl.DateTimeFormat(intlFormatterParts[0], intlFormatterParts[1]);
    const intlFormatterYParts = getDateFormatParts(this.props?.content.format, undefined, 'series');
    this.intlDateYFormatter = new Intl.DateTimeFormat(
      intlFormatterYParts[0],
      intlFormatterYParts[1],
    );
    const monthColName = getMonthColumnName(
      srcRow,
      computeKeys,
      !!(groupByDetails || segmentDataDetails),
    );
    const isSameMonth = allDataWithTheSameMonth(srcRow, dataRow, monthColName);
    this.chartGroupByMonth =
      groupByDetails === 'daily' ||
      groupByDetails === 'weekly' ||
      (groupByDetails === 'monthly' && (!isSameMonth || !isSameYear));

    const mainValueColumn = findColumn(columns, mainValue);
    const mainValueColumnName =
      mainValueColumn?.name || this.schemaService.getColumnName(mainValue);
    const groupByColumns = findColumn(columns, groupBy);
    const groupByColumnsName = groupByColumns?.name || this.schemaService.getColumnName(groupBy);
    const segmentDataColumn = findColumn(columns, segmentData);
    const segmentDataColumnName =
      segmentDataColumn?.name || this.schemaService.getColumnName(segmentData);

    const { dataRow: dataRows, hasValuesWithoutDate } = this.extendDataRowsWithMissingDates(
      dataRow,
      groupByDetails,
      computeKeys,
      monthColName,
      segmentDataColumnName,
      groupByColumnsName,
      isTimeOnlyFormatter(this.intlDateXFormatter),
    );

    const result = dataRows.map((value) => {
      const { label: labelValue, valueToSort } = getLabelForChart(
        groupByColumnsName || groupBy,
        groupByColumns?.id,
        value,
        this.intlDateXFormatter,
        this.elementType,
        groupByDetails,
        computeKeys,
      );
      let label = labelValue;
      if (!label?.toString) {
        label = '';
      }
      if (hasValuesWithoutDate && value[computeKeys.year] === null) {
        label = EMPTY_DATE_LABEL;
      }
      const { label: segmentLabelValue, valueToSort: segmentValueToSort } = segmentData
        ? getLabelForChart(
            segmentDataColumnName || segmentData,
            segmentDataColumn?.id,
            value,
            this.intlDateYFormatter,
            this.elementType,
            segmentDataDetails,
            computeKeys,
          )
        : { label: undefined, valueToSort: undefined };
      let segmentDataValue = segmentLabelValue;
      if (segmentDataValue === undefined) {
        segmentDataValue = '';
      }
      const alternativeValues = multiSeriesValues
        ?.filter((dataPoint) => dataPoint.aggregation)
        ?.map((val) => {
          if (!val.alias || !val.aggregation) {
            return;
          }

          const alias =
            val.aggregation === 'FIELD VALUE'
              ? findColumn(columns, val.mainValue)?.name
              : val.alias;

          return {
            [aliasMap[val.alias] ?? val.alias]: valueFromWorkflow(
              undefined,
              undefined,
              val.aggregation,
              value,
              alias,
              this.elementType,
            ),
          };
        });
      const result: Record<string, any> = {
        ...((mainValue || aggregation) && {
          [aliasMap[mainAlias]]: valueFromWorkflow(
            mainValueColumnName || mainValue,
            mainValueColumn?.id,
            aggregation,
            value,
            mainAlias,
            this.elementType,
          ),
        }),
        label,
        ...((segmentData || groupBy) && { segmentData: segmentDataValue }),
        valueToSort,
        segmentValueToSort,
      };
      alternativeValues?.forEach((series) => {
        if (!series) {
          return;
        }
        Object.entries(series).forEach(([key, val]) => {
          result[aliasMap[key] ?? key] = val;
        });
      });
      return result;
    });
    const isSingleSegment = !(segmentData || segmentDataDetails || (multiSeries?.length ?? 0) < 2);
    if (order === 'x' || !isSingleSegment) {
      result.sort((a, b) =>
        compareXAxis(a, b, 'valueToSort', direction === 'Ascending', !!groupByDetails),
      );
    }
    const labels = [...new Set(result.map((v) => v.label.toString()))];

    // alias is fulfilled at this step
    const aliasesForDataSegments = [aliasMap[mainAlias]];
    multiSeriesValues?.forEach(
      (series) => series.alias && aliasesForDataSegments.push(aliasMap[series.alias]),
    );

    const dataSegments = [...new Set(result.map((v) => v.segmentData))];
    const defaultValue = getMissingValueForChartType(this.elementType);
    const dataSets = dataSegments.map((segment) => {
      const dataset = result.filter(({ segmentData }) => segmentData === segment);
      const datasetFull = labels.map(
        (label) =>
          dataset.find((data) => data.label.toString() === label) || {
            label,
            ...Object.fromEntries(aliasesForDataSegments.map((alias) => [alias, defaultValue])),
            segmentData: segment,
          },
      );
      return datasetFull;
    });

    if (order === 'y' && isSingleSegment) {
      const segmentsSum: Record<string, number> = {};
      result.forEach((row) => {
        if (!segmentsSum[row.label]) {
          segmentsSum[row.label] = 0;
        }
        segmentsSum[row.label] += getValueForAlias(row, mainAlias);
      });

      const directionSign = direction === 'Ascending' ? 1 : -1;
      dataSets.forEach((dataset) => {
        dataset.sort((a, b) => directionSign * (segmentsSum[a.label] - segmentsSum[b.label]));
      });
    }

    this.chartGroupBy = groupBy;
    this.chartLabels = labels;

    const unsortedDatasetMeta = dataSets.flatMap((dataPoints) => {
      return aliasesForDataSegments.map((alias) => {
        const segmentSuffix =
          dataSegments.length > 1 ? ` (${dataPoints[0].segmentData || 'No data'})` : '';
        const label =
          aliasesForDataSegments.length > 1
            ? `${alias}${segmentSuffix}`
            : dataPoints[0].segmentData;
        return {
          dataPoints,
          alias,
          label,
        };
      });
    });

    return { unsortedDatasetMeta, customIcons };
  }

  /** Resets the rendered graphical chart so a deconfigured chart does not keep showing stale data. */
  private clearChart(): void {
    this.chartGroupBy = undefined;
    this.chartLabels = [];
    this.chartDatasets = [];
    safeDetectChanges(this.cdr);
  }

  /** Line, bar and column charts share the data model but need no icon/canvas decoration. */
  private updateGraphicalChart(columns: DatasetColumn[]): void {
    const model = this.buildChartModel(columns);
    if (!model) {
      this.clearChart();
      return;
    }

    const { datasetMeta } = prepareDatasetMeta(
      model.unsortedDatasetMeta,
      undefined,
      [],
      this.elementType,
      this.chartPaletteColors,
      (label) => this.chartService.labelToIcon(label),
    );

    this.chartDatasets = this.buildChartDatasetsFromMeta(datasetMeta);

    safeDetectChanges(this.cdr);
  }

  /**
   * Dot chart owns all icon-specific logic: resolving icons, syncing the custom-icon map and
   * pre-warming the SVG point canvases — which is the only reason this path is async.
   *
   * Returns an observable so the caller's `switchMap` can drop the result if a newer emission
   * arrives mid pre-warm: the datasets are committed in the `tap`, downstream of the await, and
   * are therefore skipped once this subscription is torn down.
   */
  private updateDotChart$(columns: DatasetColumn[]): Observable<void> {
    const model = this.buildChartModel(columns);
    if (!model) {
      this.clearChart();
      return EMPTY;
    }
    const { customIcons } = model;

    const initialIconZip: { label: string; icon: DotChartIcons }[] = customIcons?.enabled
      ? customIcons.iconMap.map(({ label, icon }) => ({
          label,
          icon: icon as DotChartIcons,
        }))
      : [];

    const { datasetMeta, svgToPrepare, labelIconZip } = prepareDatasetMeta(
      model.unsortedDatasetMeta,
      customIcons,
      initialIconZip,
      this.elementType,
      this.chartPaletteColors,
      (label) => this.chartService.labelToIcon(label),
    );

    return from(this.syncAndPrepareDotChart(labelIconZip, customIcons, svgToPrepare)).pipe(
      tap(() => {
        const labelToIcon = Object.fromEntries(
          labelIconZip.map(({ label, icon }) => [label, { icon }]),
        );

        this.chartDatasets = this.buildChartDatasetsFromMeta(datasetMeta, labelToIcon);

        safeDetectChanges(this.cdr);
      }),
      catchError(() => EMPTY),
    );
  }

  private extendDataRowsWithMissingDates(
    dataRow: any[],
    groupByDetails: string | undefined,
    computeKeys: ComputeKeys,
    monthColName: string,
    segmentDataColumnName: string | undefined,
    groupByColumnsName: string | undefined,
    isGroupByTimeFormat: boolean,
  ): { dataRow: any[]; hasValuesWithoutDate: boolean } {
    if (
      groupByDetails &&
      ['yearly', 'monthly', 'weekly', 'daily', 'quarterly'].includes(groupByDetails)
    ) {
      return this.extendWithCalendarPeriods(
        dataRow,
        groupByDetails,
        computeKeys,
        monthColName,
        segmentDataColumnName,
        groupByColumnsName,
      );
    }
    if (isGroupByTimeFormat && groupByColumnsName && segmentDataColumnName) {
      return {
        dataRow: this.extendWithTimeFormat(
          dataRow,
          computeKeys,
          groupByColumnsName,
          segmentDataColumnName,
        ),
        hasValuesWithoutDate: false,
      };
    }
    return { dataRow, hasValuesWithoutDate: false };
  }

  private extendWithCalendarPeriods(
    dataRow: any[],
    groupByDetails: string,
    computeKeys: ComputeKeys,
    monthColName: string,
    segmentDataColumnName: string | undefined,
    groupByColumnsName: string | undefined,
  ): { dataRow: any[]; hasValuesWithoutDate: boolean } {
    let hasValuesWithoutDate = false;
    const fillClone = deepClone(dataRow[0] ?? {});
    Object.keys(fillClone).forEach((key) => {
      if (
        ![
          computeKeys.year,
          computeKeys.month,
          computeKeys.details,
          segmentDataColumnName,
          groupByColumnsName,
        ].includes(key)
      ) {
        fillClone[key] = 0;
      }
    });
    let yearsRange = dataRow.map((r) => r[computeKeys.year]);

    // when years range includes null, we need to extract this record from range creation
    // and later unshift a value with EMPTY_DATE_LABEL
    if (yearsRange.includes(null)) {
      hasValuesWithoutDate = true;
      yearsRange = yearsRange.filter((r) => r !== null);
    }

    const lowestYear = Math.min(...yearsRange);
    const highestYear = Math.max(...yearsRange);

    if (!yearsRange.length) {
      return { dataRow, hasValuesWithoutDate };
    }
    if (groupByDetails === 'yearly') {
      if (!isNaN(highestYear) && !isNaN(lowestYear)) {
        dataRow = fillDataRowsWithMissingYears(
          highestYear,
          lowestYear,
          fillClone,
          computeKeys,
          dataRow,
        );
      }
    } else if (groupByDetails === 'quarterly') {
      dataRow = fillDataRowsWithMissingQuarters(
        highestYear,
        lowestYear,
        fillClone,
        computeKeys,
        dataRow,
      );
    } else if (groupByDetails === 'monthly') {
      dataRow = fillDataRowsWithMissingMonths(
        highestYear,
        lowestYear,
        fillClone,
        computeKeys,
        monthColName,
        dataRow,
      );
    } else if (groupByDetails === 'weekly') {
      dataRow = fillDataRowsWithMissingWeeks(
        highestYear,
        lowestYear,
        fillClone,
        computeKeys,
        dataRow,
      );
    } else if (groupByDetails === 'daily') {
      dataRow = fillDataRowsWithMissingDays(
        highestYear,
        lowestYear,
        fillClone,
        computeKeys,
        monthColName,
        dataRow,
      );
    }
    return { dataRow, hasValuesWithoutDate };
  }

  private extendWithTimeFormat(
    dataRow: any[],
    computeKeys: ComputeKeys,
    groupByColumnsName: string,
    segmentDataColumnName: string,
  ): any[] {
    const uniqSegmentBy = [...new Set(dataRow.map((r) => r[segmentDataColumnName]))];
    const fillClones = uniqSegmentBy.map((segment) => {
      const fillClone = deepClone(dataRow[0] ?? {});
      fillClone[segmentDataColumnName] = segment;
      Object.keys(fillClone).forEach((key) => {
        if (
          ![
            computeKeys.year,
            computeKeys.month,
            computeKeys.details,
            segmentDataColumnName,
            groupByColumnsName,
          ].includes(key)
        ) {
          // Missing values: NaN for dot chart, 0 for non-dot charts.
          fillClone[key] = getMissingValueForChartType(this.elementType);
        }
      });
      return fillClone;
    });
    const { dataRows, timeDiff: minDiff } = fillTimeRowsWithMissingMinutes(
      dataRow,
      groupByColumnsName,
      segmentDataColumnName,
      fillClones,
    );
    this.chartGridTimeDiff = minDiff;
    this.refreshDotChartGridDense();
    return dataRows;
  }

  private buildChartDatasetsFromMeta(
    datasetMeta: {
      dataPoints: Record<string, any>[];
      alias: string;
      label: any;
      backgroundColor: string;
      hoveredBg: string;
      notHoveredBg: string;
      iconNotHoveredBg: string;
    }[],
    labelToIcon?: Record<string, { icon: DotChartIcons }>,
  ): {
    data: number[];
    label: any;
    originalColors: {
      backgroundColor: string;
      borderColor: string;
    };
    hoveredColors: {
      backgroundColor: string;
      borderColor: string;
    };
    notHoveredColors: {
      backgroundColor: string;
      borderColor: string;
    };
    pointCanvas: HTMLCanvasElement | null | undefined;
    hoveredPointCanvas: HTMLCanvasElement | null | undefined;
    notHoveredPointCanvas: HTMLCanvasElement | null | undefined;
  }[] {
    const iconSize = this.dotChartIconSize();
    return datasetMeta.map(
      ({
        dataPoints,
        alias,
        backgroundColor,
        hoveredBg,
        notHoveredBg,
        iconNotHoveredBg,
        label,
      }) => {
        // normal color => original
        // border => original
        const originalColors = { backgroundColor, borderColor: backgroundColor };
        const pointCanvas = labelToIcon
          ? this.chartService.getCachedCanvas(labelToIcon[label].icon, backgroundColor, iconSize)
          : null;

        // hovered + cursor => original dim by 24% #000000
        // border => original dim by 24% #000000
        const hoveredColors = { backgroundColor: hoveredBg, borderColor: hoveredBg };
        const hoveredPointCanvas = labelToIcon
          ? this.chartService.getCachedCanvas(labelToIcon[label].icon, backgroundColor, iconSize)
          : null;

        // hovered - cursor => original light to 88% #ffffff
        // border => original light to 88% #ffffff
        const notHoveredColors = { backgroundColor: notHoveredBg, borderColor: notHoveredBg };
        const notHoveredPointCanvas = labelToIcon
          ? this.chartService.getCachedCanvas(labelToIcon[label].icon, iconNotHoveredBg, iconSize)
          : null;

        return {
          data: dataPoints.map((dataValue) => getValueForAlias(dataValue, alias)),
          label,
          originalColors,
          hoveredColors,
          notHoveredColors,
          pointCanvas,
          hoveredPointCanvas,
          notHoveredPointCanvas,
        };
      },
    );
  }

  private dotChartIconSize(): number {
    const iconSize = this.props?.style?.chart?.iconSize;
    const expectedIconSize = (!!iconSize && Number.parseInt(iconSize, 10)) || 8;
    return expectedIconSize;
  }

  private async syncAndPrepareDotChart(
    labelIconZip: {
      label: string;
      icon: DotChartIcons;
    }[],
    customIcons: ChartPropsModelData['customIcons'],
    svgToPrepare: { icon: DotChartIcons; colors: string[] }[],
  ): Promise<void> {
    if (customIcons?.enabled) {
      this.syncCustomIconMap(labelIconZip, customIcons);
    }

    // pre-warm all canvases in parallel (single await)
    await Promise.all(
      svgToPrepare.flatMap(({ icon, colors }) =>
        colors.map((color) =>
          this.chartService
            .prepareSvgForUse(icon, color, this.dotChartIconSize())
            .catch(() => null),
        ),
      ),
    );
  }

  private syncCustomIconMap(
    labelIconZip: {
      label: string;
      icon: DotChartIcons;
    }[],
    customIcons: {
      enabled: boolean;
      iconMap: {
        label: string;
        icon: string;
      }[];
    },
  ): void {
    const storedMap = customIcons?.iconMap;
    const newMap = labelIconZip.map(({ label, icon }) => ({
      label,
      icon,
    }));
    // When there i only one series and it has no label we have mismatch effect between what we have in props and what we have in the data.
    // In this case we don't want to update the iconMap because it will push invalid data to the props.
    // We need either label or more than one series to update the iconMap.
    const isStandaloneSeriesWithoutLabel = newMap.length === 1 && newMap[0].label === '';
    if (!this.isCustomIconMapEqual(storedMap, newMap) && !isStandaloneSeriesWithoutLabel) {
      this.updateProp.emit({
        path: 'content.data.customIcons.iconMap',
        payload: newMap,
      });
    }
  }

  //#endregion

  //#region pie chart
  private updatePieChart(columns: DatasetColumn[]): void {
    const { data } = this.props?.content || {};
    const { aggregation, mainValue, segmentData, segmentDataDetails } = data || {};
    let { alias } = data || {};
    if (!alias) {
      alias = undefined;
    }

    if (!aggregation || !segmentData) {
      this.chartSegmentData = undefined;
      return;
    }

    const dataRow = Array.isArray(this.value) ? this.value : [];
    if (!dataRow.length) {
      this.noData = true;
      return;
    } else {
      this.noData = false;
    }

    const srcRow = dataRow[0];

    const computeKeys = getKeysToReadDataFromValue(srcRow);

    const isSameYear = allDataWithTheSameYear(dataRow[0], dataRow, computeKeys);
    const intlFormatterParts = getDateFormatParts(
      this.props?.content.format,
      isSameYear,
      undefined,
    );
    this.intlDateXFormatter = new Intl.DateTimeFormat(intlFormatterParts[0], intlFormatterParts[1]);

    const isSameMonth = allDataWithTheSameMonth(dataRow[0], dataRow, computeKeys.month);
    this.chartGroupByMonth =
      (segmentDataDetails === 'monthly' || segmentDataDetails === 'daily') && !isSameMonth;

    const mainValueColumn = findColumn(columns, mainValue);
    const mainValueColumnName =
      mainValueColumn?.name || this.schemaService.getColumnName(mainValue);
    const segmentDataColumn = findColumn(columns, segmentData);
    const segmentDataColumnName =
      segmentDataColumn?.name || this.schemaService.getColumnName(segmentData);
    const hasValuesWithoutDate =
      segmentDataDetails &&
      ['yearly', 'monthly', 'weekly', 'daily', 'quarterly'].includes(segmentDataDetails) &&
      dataRow.some((val) => val[computeKeys.year] === null);
    const result = dataRow.map((value) => {
      let { label } = getLabelForChart(
        segmentDataColumnName || segmentData,
        segmentDataColumn?.id,
        value,
        this.intlDateXFormatter,
        this.elementType,
        segmentDataDetails,
        computeKeys,
      );
      if (hasValuesWithoutDate && value[computeKeys.year] === null) {
        label = EMPTY_DATE_LABEL;
      }
      if (!label?.toString) {
        label = '';
      }
      if (typeof label !== 'string') {
        label = (label as any).toString?.() ?? label;
      }
      return {
        ...((mainValue || aggregation) && {
          mainValue: valueFromWorkflow(
            mainValueColumnName || mainValue,
            mainValueColumn?.id,
            aggregation,
            value,
            alias,
            this.elementType,
          ),
        }),
        label,
        segmentData: '',
      };
    });
    if (!result.length) {
      return;
    }
    result.sort((a, b) => compareXAxis(a, b, 'label', true, !!segmentDataDetails));
    const labels = [...new Set(result.map((v) => v.label.toString()))];
    const dataSegments = [''];
    const dataSets = dataSegments.map((segment) => {
      const dataset = result.filter(({ segmentData }) => segmentData === segment);
      const datasetFull = labels.map(
        (label) =>
          dataset.find((data) => data.label === label) || {
            label,
            mainValue: 0,
            segmentData: segment,
          },
      );
      return datasetFull;
    });
    this.chartSegmentData = segmentData;
    this.chartLabels = labels;
    const chartDatasets = dataSets.map((v) => {
      const backgroundColor = v.map(
        (_, i) =>
          (this.chartPaletteColors &&
            this.chartPaletteColors[i % this.chartPaletteColors.length]) ||
          '',
      );
      // normal color => original
      // border => original
      const originalColors = {
        backgroundColor,
        borderColor: backgroundColor,
      };

      // hovered + cursor => original
      // border => original
      const hoveredColors = {
        backgroundColor: backgroundColor,
        borderColor: backgroundColor,
      };

      // hovered - cursor => original light to 88% #ffffff
      // border => original light to 88% #ffffff
      const notHoveredBg = backgroundColor.map((color) => adjustColorBrightness(color, 12));
      const notHoveredColors = {
        backgroundColor: notHoveredBg,
        borderColor: notHoveredBg,
      };
      return {
        label: v[0].segmentData,
        data: v.map((dataValue) => dataValue.mainValue) || 0,

        originalColors,
        hoveredColors,
        notHoveredColors,
      };
    });
    this.chartDatasets = chartDatasets;

    safeDetectChanges(this.cdr);
  }
  //#endregion

  private updateValuesStyles(): void {
    // BL-2584
    // let color = '';
    // if (this.deltaValue > 0 && this.alertLogic || this.deltaValue < 0 && !this.alertLogic) {
    //   color = this.colorAlertSuccess || '';
    // } else if (this.deltaValue < 0 && this.alertLogic || this.deltaValue > 0 && !this.alertLogic) {
    //   color = this.colorAlertDanger || '';
    // } else if (this.deltaValue === 0) {
    //   color = this.colorAlertNeutral || '';
    // }
    // this.valueColor = this.colorMainValue ? color : this.colorGeneralValue ? this.colorGeneralValue : undefined;
    // this.deltaColor = color;
    // this.valueOutline = this.colorChartOutline || this.styleBgOutline !== 'none' ? `0 0 0 ${this.styleBgOutlineWidth} ${this.colorChartOutline && color || this.styleBgOutline}` : undefined;
    const { content, style } = (this.props as ChartValuePropsModel | undefined) || {};
    const { general } = style || {};

    if (!general) {
      return;
    }

    this.valueStyles = {
      color: general?.value?.color1 ?? '',
    };

    const fontSizeEnabled = style?.valueFont?.fontSizeEnabled ?? false;
    const valueFont = makeFontStyles(style?.valueFont) ?? {};
    valueFont['font-size'] = fontSizeEnabled ? valueFont['font-size'] : '';
    valueFont['line-height'] = fontSizeEnabled ? 'normal' : '';
    this.fontSizeEnabled = fontSizeEnabled;
    this.valueAlign = style?.general?.align ?? 'left';
    this.valueMinHeightEnabled = content?.options?.valueMinHeightEnabled ?? false;

    if (this.elementType === 'chartSingle') {
      this.valueStyles = {
        ...this.valueStyles,
        ...valueFont,
        ...makeFontStyleStyles(style?.valueFont?.style, '700'),
        ['font-weight']: this.fontsService.getFontWeight(
          style?.valueFont?.fontFamily,
          style?.valueFont?.fontWeight,
        ),
      };
    }
  }

  private shouldSkipThisChange(changes: SimpleChanges): boolean {
    const previousContent = changes.props?.previousValue?.content;
    const currentContent = changes.props?.currentValue?.content;

    const previousData = previousContent?.data;
    const currentData = currentContent?.data;

    const previousFormat = previousContent?.format;
    const currentFormat = currentContent?.format;

    const previousAlert = previousContent?.alert;
    const currentAlert = currentContent?.alert;
    if (
      ['direction', 'order'].find(
        (prop) => previousData && previousData[prop] !== currentData && currentData[prop],
      ) ||
      ['delta', 'type', 'precision', 'currency'].find(
        (prop) => previousFormat && previousFormat[prop] !== currentFormat && currentFormat[prop],
      ) ||
      ['colorMainValue', 'colorChartOutline', 'logic'].find(
        (prop) => previousAlert && previousAlert[prop] !== currentAlert && currentAlert[prop],
      )
    ) {
      return false;
    }
    return (
      !changes.value &&
      changes.props &&
      !changes.props.firstChange &&
      !(
        previousData &&
        currentData &&
        Object.keys(previousData).length &&
        Object.keys(currentData).length &&
        Object.keys(currentData).every((key) => currentData[key] === previousData[key])
      )
    );
  }

  private getPropsColumns(): Observable<DatasetColumn[]> {
    return this.updateElementType$.pipe(
      switchMap(() => {
        const dataSource = this.props?.data?.find((data) => data.key === 'value')?.dataSource;
        if (dataSource) {
          return this.store.select(runtimeSelectors.selectWorkflowById(dataSource?.id)).pipe(
            switchMap((workflow) => {
              if (!workflow) {
                return this.schemaService
                  .obtainSchema(dataSource.id, dataSource.sourceType === 'context')
                  .pipe(map((schema) => schema?.columns || []));
              }
              const aggregationSchema = this.intelliSenseService.getSchemaForWorkflowAtStep(
                workflow,
                isAggregateNode,
              );
              // for external dataSource we use compute to get values so we need to get unchanged schema
              const computeSchema = this.intelliSenseService.getSchemaForWorkflowAtStep(
                workflow,
                isComputeNode,
              );
              return of(aggregationSchema?.columns || computeSchema?.columns || []);
            }),
          );
        }
        return of([]);
      }),
    );
  }

  private createSchemaForElementOutput(): ElementSchema {
    const columns: BlazeSchema['columns'] = [];
    if (this.elementType === 'chartSingle') {
      columns.push({
        columnType: 'Number',
        dataType: 'Number',
        name: 'value',
        isProfilePicture: false,
      });
    } else {
      columns.push({
        columnType: 'Number',
        dataType: 'Number',
        name: 'value',
        isProfilePicture: false,
      });
      if (this.props?.content.data?.groupBy) {
        columns.push({
          columnType: 'String',
          dataType: 'String',
          name: 'label',
          isProfilePicture: false,
        });
      }
      if (this.props?.content.data?.segmentData) {
        const name = ['chartPie', 'chartDoughnut'].includes(this.elementType) ? 'label' : 'series';
        columns.push({
          columnType: 'String',
          dataType: 'String',
          name,
          isProfilePicture: false,
        });
      }
    }
    return this.createElementSchema('select', columns);
  }

  private setSchemaToOutput(): void {
    const schema = this.createSchemaForElementOutput();
    this.setDefaultEventSchema('select', schema);
  }

  private updateOutput(value: ChartValueUpdate): void {
    const isInitialData = !this.wasEmitted && typeof value !== 'undefined' && value !== null;
    const schema = this.getEventSchema('select');
    let data;
    if (isNullish(value)) {
      data = null;
    } else if (this.elementType === 'chartSingle' && isSingleChartUpdateData(value)) {
      data = { value };
    } else if (this.elementType !== 'chartSingle' && isGraphicalChartUpdateData(value)) {
      data = {
        label: value.label,
        value: value.value,
        ...(value.series && { series: value.series }),
      };
    } else {
      console.error(
        `Unsupported type: ${this.elementType} or data value doesn't match expected output.`,
      );
      return;
    }
    const emitData: CanvasElementEvent = {
      eventType: 'select',
      data,
      schema,
      isInitialData,
    };
    if (this.outputEmitTracker.shouldEmit(emitData.data)) {
      this.output.emit(emitData);
      this.wasEmitted = !!value;
    }
  }

  private isChartConfigured(elementType: ChartVariantType, data?: ChartPropsModelData): boolean {
    const { aggregation, mainValue, groupBy, segmentData } = data || {};
    const isAggregationConfigured = aggregation === 'COUNT' || (aggregation && mainValue);
    const isBarChartConfigured =
      ['chartColumn', 'chartBar', 'chartLine', 'chartDot'].includes(elementType) && groupBy;
    const isPieChartConfigured = ['chartPie', 'chartDoughnut'].includes(elementType) && segmentData;
    const isSingleChartConfigured = elementType === 'chartSingle';
    return !!(
      isAggregationConfigured &&
      (isBarChartConfigured || isPieChartConfigured || isSingleChartConfigured)
    );
  }

  private isCustomIconMapEqual(
    map1: { icon: string; label: string }[] | undefined,
    map2: { icon: string; label: string }[] | undefined,
  ): boolean {
    if (!map1 && !map2) {
      return true;
    }
    if (!map1 || !map2 || map1?.length !== map2?.length) {
      return false;
    }
    const sortedMap1 = [...map1].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    const sortedMap2 = [...map2].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return sortedMap1.every(
      (item, index) =>
        item.label === sortedMap2[index].label && item.icon === sortedMap2[index].icon,
    );
  }

  private refreshDotChartGridDense(): void {
    const { style } = this.props || {};
    if (!style) {
      return;
    }
    if (this.elementType === 'chartDot' && style.chart?.gridDensity === 'dense') {
      const { data } = this.props?.content || {};
      const { groupByDetails } = data || {};
      if (groupByDetails === 'daily') {
        this.chartGridDensity = 1 / 24;
      } else if (groupByDetails === 'weekly') {
        this.chartGridDensity = 1 / 7;
      } else if (groupByDetails === 'monthly') {
        this.chartGridDensity = 1 / 30;
      } else if (groupByDetails === 'quarterly') {
        this.chartGridDensity = 1 / 3;
      } else if (groupByDetails === 'yearly') {
        this.chartGridDensity = 1 / 12;
      } else if (
        this.intlDateXFormatter &&
        isTimeOnlyFormatter(this.intlDateXFormatter) &&
        this.chartGridTimeDiff
      ) {
        this.chartGridDensity = 1 / this.chartGridTimeDiff;
        safeDetectChanges(this.cdr);
      } else {
        this.chartGridDensity = 1;
      }
    } else {
      this.chartGridDensity = 1;
    }
  }
}

function getAbsoluteDimensions(el: HTMLElement | null | undefined): {
  width: number;
  height: number;
} {
  if (!el) {
    return { width: 0, height: 0 };
  }

  const elStyles = getComputedStyle(el);
  const width =
    el.offsetWidth +
    Number.parseFloat(elStyles.marginLeft) +
    Number.parseFloat(elStyles.marginRight);
  const height =
    el.offsetHeight +
    Number.parseFloat(elStyles.marginTop) +
    Number.parseFloat(elStyles.marginBottom);
  return { width, height };
}
